// TransactionsReport.tsx — Unified search + export view
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { reportService, TransactionFilters } from '../../services/reportService';
import LoadingScreen from '../LoadingScreen';
import {
  Search, Download, RefreshCw, ChevronLeft, ChevronRight,
  Filter, Calendar, X, Loader, ChevronUp, ChevronDown,
  FileText, FileSpreadsheet, CheckCircle, XCircle, Clock,
  AlertCircle
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';

interface ExportJob {
  job_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number | null;
  total_rows: number | null;
  file_size: number | null;
  filename: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  compressed?: boolean;
}

const TransactionsReport: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 1, totalItems: 0, limit: 20
  });

  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const [advancedFilters, setAdvancedFilters] = useState({
    customer_details: '', thirdparty_reference: '', paydrc_reference: '',
    currency: '', method: '', status: '', action: ''
  });

  // Export state
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [exporting, setExporting] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const currencyOptions = ['USD', 'CDF'];
  const methodOptions = ['m-pesa', 'airtel', 'orange', 'afrimoney', 'mtn', 'vodacom'];
  const statusOptions = ['Successful', 'Failed', 'Pending', 'Submitted'];
  const actionOptions = ['credit', 'debit'];

  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getDateOffset = (days: number) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  // Init default dates: last 7 days
  useEffect(() => {
    setDateRange({ start: getDateOffset(6), end: getTodayDate() });
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Auto-scroll to export progress
  useEffect(() => {
    if (exportJob && progressRef.current) {
      progressRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [exportJob?.status]);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const sortedTransactions = React.useMemo(() => {
    if (!sortConfig) return transactions;
    return [...transactions].sort((a, b) => {
      let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
      if (sortConfig.key === 'created_at') { aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime(); }
      if (sortConfig.key === 'amount') { aVal = parseFloat(aVal) || 0; bVal = parseFloat(bVal) || 0; }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, sortConfig]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const buildFilters = (page = 1): TransactionFilters => {
    const f: TransactionFilters = {
      merchant_code: user?.merchant_code,
      date_from: dateRange.start, date_to: dateRange.end,
      page, limit: pagination.limit
    };
    if (advancedFilters.customer_details.trim()) f.customer_details = advancedFilters.customer_details.trim();
    if (advancedFilters.thirdparty_reference.trim()) f.thirdparty_reference = advancedFilters.thirdparty_reference.trim();
    if (advancedFilters.paydrc_reference.trim()) f.paydrc_reference = advancedFilters.paydrc_reference.trim();
    if (advancedFilters.currency) f.currency = advancedFilters.currency;
    if (advancedFilters.method) f.method = advancedFilters.method;
    if (advancedFilters.status) f.status = advancedFilters.status;
    if (advancedFilters.action) f.action = advancedFilters.action;
    return f;
  };

  const loadData = useCallback(async (page = 1) => {
    if (!user?.merchant_code || !dateRange.start || !dateRange.end) return;
    try {
      setLoading(true);
      const result = await reportService.searchTransactions(buildFilters(page));
      setTransactions(result.transactions || []);
      setPagination({ currentPage: result.page, totalPages: result.total_pages, totalItems: result.total, limit: result.limit });
    } catch (error) {
      console.error('Error loading transactions:', error);
      alert('Error loading transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.merchant_code, dateRange, advancedFilters, pagination.limit]);

  useEffect(() => {
    if (dateRange.start && dateRange.end) loadData(1);
  }, [dateRange.start, dateRange.end]);

  const handleSearch = () => loadData(1);
  const handlePageChange = (p: number) => { if (p >= 1 && p <= pagination.totalPages) loadData(p); };

  const handleQuickFilter = (start: string, end: string) => {
    setDateRange({ start, end });
  };

  const handleReset = () => {
    setDateRange({ start: getDateOffset(6), end: getTodayDate() });
    setAdvancedFilters({ customer_details: '', thirdparty_reference: '', paydrc_reference: '', currency: '', method: '', status: '', action: '' });
    setExportJob(null);
    setExporting(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  // === EXPORT ===
  const startPolling = (jobId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(API_URL + '/async-export/status/' + jobId, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (!res.ok) return;
        const job: ExportJob = await res.json();
        setExportJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setExporting(false);
        }
      } catch (err) { console.error('Poll error:', err); }
    }, 2000);
  };

  const handleExport = async () => {
    if (!dateRange.start || !dateRange.end) { alert('Please select a date range'); return; }
    try {
      setExporting(true);
      setExportJob(null);
      const token = localStorage.getItem('token');
      const body: any = {
        merchant_code: user?.merchant_code || '',
        date_from: dateRange.start, date_to: dateRange.end,
        format: exportFormat
      };
      if (advancedFilters.currency) body.currency = advancedFilters.currency;
      if (advancedFilters.method) body.method = advancedFilters.method;
      if (advancedFilters.action) body.action = advancedFilters.action;
      if (advancedFilters.status) body.status = advancedFilters.status;
      if (advancedFilters.thirdparty_reference.trim()) body.thirdparty_reference = advancedFilters.thirdparty_reference.trim();
      if (advancedFilters.paydrc_reference.trim()) body.paydrc_reference = advancedFilters.paydrc_reference.trim();
      if (advancedFilters.customer_details.trim()) body.customer_details = advancedFilters.customer_details.trim();

      const res = await fetch(API_URL + '/async-export/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify(body)
      });
      if (!res.ok) { const err = await res.json().catch(() => ({ detail: 'Export failed' })); throw new Error(err.detail); }
      const job: ExportJob = await res.json();
      setExportJob(job);
      startPolling(job.job_id);
    } catch (error: any) {
      alert(error.message || 'Export failed');
      setExporting(false);
    }
  };

  const handleDownload = () => {
    if (!exportJob || exportJob.status !== 'completed') return;
    const url = API_URL + '/async-export/download/' + exportJob.job_id;
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDateTime = (d: string) => new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const formatAmount = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };
  const formatNumber = (n: number | null) => n === null || n === undefined ? '...' : n.toLocaleString();

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes('success')) return 'bg-green-100 text-green-800 border-green-200';
    if (s.includes('fail')) return 'bg-red-100 text-red-800 border-red-200';
    if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getMethodColor = (method: string) => {
    const m = method.toLowerCase();
    if (m.includes('orange')) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (m.includes('airtel')) return 'bg-red-100 text-red-800 border-red-200';
    if (m.includes('m-pesa') || m.includes('mpesa') || m.includes('vodacom')) return 'bg-green-100 text-green-800 border-green-200';
    if (m.includes('afrimoney')) return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  return (
    <div className="space-y-6">
      {/* Quick Date Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => handleQuickFilter(getTodayDate(), getTodayDate())} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors">Today</button>
        <button onClick={() => handleQuickFilter(getDateOffset(1), getDateOffset(1))} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">Yesterday</button>
        <button onClick={() => handleQuickFilter(getDateOffset(6), getTodayDate())} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors">Last 7 Days</button>
        <button onClick={() => handleQuickFilter(getDateOffset(29), getTodayDate())} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors">Last 30 Days</button>
        <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 bg-white text-gray-700 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-2">
          <Filter className="h-4 w-4" /> {showFilters ? 'Hide Filters' : 'More Filters'}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-600" /> Date Range & Filters
            </h3>
            <button onClick={handleReset} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
              <X className="h-3 w-3" /> Reset
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                max={getTodayDate()} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                max={getTodayDate()} min={dateRange.start} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
              <select value={advancedFilters.currency} onChange={(e) => setAdvancedFilters({...advancedFilters, currency: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <option value="">All</option>
                {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
              <select value={advancedFilters.method} onChange={(e) => setAdvancedFilters({...advancedFilters, method: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <option value="">All</option>
                {methodOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={advancedFilters.status} onChange={(e) => setAdvancedFilters({...advancedFilters, status: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <option value="">All</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
              <select value={advancedFilters.action} onChange={(e) => setAdvancedFilters({...advancedFilters, action: e.target.value})}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                <option value="">All</option>
                {actionOptions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
              <input type="text" value={advancedFilters.customer_details} onChange={(e) => setAdvancedFilters({...advancedFilters, customer_details: e.target.value})}
                placeholder="Phone / name..." className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">PayDRC Ref.</label>
              <input type="text" value={advancedFilters.paydrc_reference} onChange={(e) => setAdvancedFilters({...advancedFilters, paydrc_reference: e.target.value})}
                placeholder="PayDRC ref..." className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Merchant Ref.</label>
              <input type="text" value={advancedFilters.thirdparty_reference} onChange={(e) => setAdvancedFilters({...advancedFilters, thirdparty_reference: e.target.value})}
                placeholder="Your reference..." className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
            </div>
          </div>

          {/* Action Row: Search + Export */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-gray-100">
            <button onClick={handleSearch} disabled={loading}
              className="flex items-center justify-center px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
              <Search className="h-4 w-4 mr-2" /> Search
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button onClick={() => setExportFormat('csv')}
                  className={'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ' + (exportFormat === 'csv' ? 'bg-white shadow text-green-700' : 'text-gray-600')}>
                  <FileText className="h-3 w-3" /> CSV
                </button>
                <button onClick={() => setExportFormat('excel')}
                  className={'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ' + (exportFormat === 'excel' ? 'bg-white shadow text-blue-700' : 'text-gray-600')}>
                  <FileSpreadsheet className="h-3 w-3" /> Excel
                </button>
              </div>
              <button onClick={handleExport} disabled={exporting || !dateRange.start || !dateRange.end}
                className="flex items-center px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium">
                {exporting ? <Loader className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Export {exportFormat.toUpperCase()}
              </button>
            </div>
          </div>

          {/* Export Progress — RIGHT BELOW the buttons */}
          {exportJob && (
            <div ref={progressRef} className="mt-4 bg-gradient-to-br from-gray-50 to-green-50 rounded-xl border-2 border-green-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  {exportJob.status === 'queued' && <Clock className="h-4 w-4 text-yellow-500" />}
                  {exportJob.status === 'processing' && <Loader className="h-4 w-4 text-blue-500 animate-spin" />}
                  {exportJob.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                  {exportJob.status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                  Export Status
                </h4>
                <span className={'px-2 py-0.5 rounded-full text-xs font-semibold ' + (
                  exportJob.status === 'completed' ? 'bg-green-100 text-green-700' :
                  exportJob.status === 'failed' ? 'bg-red-100 text-red-700' :
                  exportJob.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                  'bg-yellow-100 text-yellow-700'
                )}>{exportJob.status.toUpperCase()}</span>
              </div>

              {(exportJob.status === 'processing' || exportJob.status === 'queued') && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{exportJob.total_rows ? 'Processing ' + formatNumber(exportJob.total_rows) + ' transactions...' : 'Counting transactions...'}</span>
                    {exportJob.total_rows && exportJob.progress !== null && (
                      <span>{Math.round((exportJob.progress / exportJob.total_rows) * 100)}%</span>
                    )}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2.5 rounded-full transition-all duration-500 animate-pulse"
                      style={{ width: exportJob.total_rows && exportJob.progress !== null ? Math.min(100, (exportJob.progress / exportJob.total_rows) * 100) + '%' : '30%' }} />
                  </div>
                </div>
              )}

              {exportJob.status === 'completed' && (
                <div className="space-y-3">
                  <div className="flex gap-3 text-sm">
                    <span className="bg-green-50 text-green-700 px-3 py-1 rounded-lg border border-green-200">
                      {formatNumber(exportJob.progress)} rows
                    </span>
                    <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg border border-blue-200">
                      {formatSize(exportJob.file_size)}
                    </span>
                    {exportJob.compressed && (
                      <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg border border-emerald-200">
                        Compressed
                      </span>
                    )}
                  </div>
                  <button onClick={handleDownload}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90 font-semibold">
                    <Download className="h-4 w-4 mr-2" /> Download ({formatSize(exportJob.file_size)})
                  </button>
                </div>
              )}

              {exportJob.status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{exportJob.error || 'Export failed.'}</p>
                  <button onClick={() => { setExportJob(null); setExporting(false); }}
                    className="mt-2 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Try Again</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Transactions</h3>
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold">{transactions.length > 0 ? (pagination.currentPage - 1) * pagination.limit + 1 : 0}</span> to{' '}
                <span className="font-semibold">{Math.min(pagination.currentPage * pagination.limit, pagination.totalItems)}</span> of{' '}
                <span className="font-semibold">{pagination.totalItems.toLocaleString()}</span> results
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Show:</span>
              <select value={pagination.limit} onChange={(e) => { setPagination(p => ({...p, limit: Number(e.target.value), currentPage: 1})); }}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-purple-500">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button onClick={() => loadData(pagination.currentPage)} disabled={loading}
                className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                <RefreshCw className={'h-4 w-4 mr-1 ' + (loading ? 'animate-spin' : '')} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingScreen fullscreen={false} message="Loading transactions..." />
        ) : transactions.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100" onClick={() => requestSort('created_at')}>
                      <div className="flex items-center">Date & Time
                        {sortConfig?.key === 'created_at' && (sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />)}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">PayDRC Ref.</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Merchant Ref.</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100" onClick={() => requestSort('amount')}>
                      <div className="flex items-center">Amount
                        {sortConfig?.key === 'amount' && (sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />)}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedTransactions.map((tx) => (
                    <tr key={tx.id + '-' + tx.paydrc_reference} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDateTime(tx.created_at)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900"><div className="max-w-xs truncate" title={tx.customer_details}>{tx.customer_details || 'N/A'}</div></td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900"><div className="max-w-xs truncate" title={tx.paydrc_reference}>{tx.paydrc_reference}</div></td>
                      <td className="px-6 py-4 text-sm text-gray-900"><div className="max-w-xs truncate" title={tx.thirdparty_reference}>{tx.thirdparty_reference || 'N/A'}</div></td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatAmount(parseFloat(tx.amount) || 0)} {tx.currency}</td>
                      <td className="px-6 py-4">
                        <span className={'inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ' + getMethodColor(tx.method)}>{tx.method}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={'inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ' + getStatusColor(tx.status)}>{tx.status}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{tx.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Page <span className="font-semibold">{pagination.currentPage}</span> of <span className="font-semibold">{pagination.totalPages}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1}
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">First</button>
                  <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                  </button>
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pn;
                    if (pagination.totalPages <= 5) pn = i + 1;
                    else if (pagination.currentPage <= 3) pn = i + 1;
                    else if (pagination.currentPage >= pagination.totalPages - 2) pn = pagination.totalPages - 4 + i;
                    else pn = pagination.currentPage - 2 + i;
                    return (
                      <button key={pn} onClick={() => handlePageChange(pn)}
                        className={'px-3 py-1.5 text-sm font-medium rounded-lg ' + (pagination.currentPage === pn ? 'bg-purple-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50')}>
                        {pn}
                      </button>
                    );
                  })}
                  <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}
                    className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                  <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages}
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Last</button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <Search className="h-16 w-16 text-gray-300 mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h4>
            <p className="text-gray-600 text-center max-w-md mb-6">Try adjusting your search criteria or selecting a different date range.</p>
            <button onClick={handleReset} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Reset Filters</button>
          </div>
        )}
      </div>


    </div>
  );
};

export default TransactionsReport;
